import React, { useCallback, useEffect, useRef, useState } from "react";
import { TextMessage } from "./TextMessage";
import { IS_AT_BOTTOM_OFFSET } from "@/configs/constants";
import { Product } from "@/types/product";
import { UnifiedMessage } from "@/hooks/use-messages";
import { ProductList } from "./ProductList";
import { getValue } from "@/utils/storage";
import { AgentState } from "@livekit/components-react";
import { ThinkingIndicator } from "./ThinkingIndicator";
import { useIsAgentThinking } from "@/hooks/use-is-agent-thinking";

interface ChatMessagesProps {
  messages: UnifiedMessage[];
  onSelectProduct: (product: Product) => void;
  triggerScrollToBottomCounter?: number;
  agentState: AgentState;
}

const ChatMessages: React.FC<ChatMessagesProps> = ({
  messages,
  onSelectProduct,
  triggerScrollToBottomCounter,
  agentState,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showNewMessagesPrompt, setShowNewMessagesPrompt] =
    React.useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [newMessagesCount, setNewMessagesCount] = React.useState(0);
  const prevMessagesLength = React.useRef(messages.length);
  const prevScrollHeight = React.useRef(0);
  const prevScrollTop = React.useRef(0);
  const isThinking = useIsAgentThinking(agentState);

  // Record scroll position before messages update
  useEffect(() => {
    const refElement = scrollRef.current;
    if (!refElement) return;

    prevScrollHeight.current = refElement.scrollHeight;
    prevScrollTop.current = refElement.scrollTop;
  }, [messages.length]);

  useEffect(() => {
    if (triggerScrollToBottomCounter === undefined) return;

    // Scroll to bottom when trigger changes
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
    setShowNewMessagesPrompt(false);
    setNewMessagesCount(0);
    setIsAtBottom(true);
  }, [triggerScrollToBottomCounter]);

  useEffect(() => {
    const refElement = scrollRef.current;
    if (!refElement) return;

    const isAtBottom = () =>
      refElement.scrollHeight - refElement.scrollTop - refElement.clientHeight <
      IS_AT_BOTTOM_OFFSET;

    let userAtBottom = true;

    const handleUserScroll = () => {
      userAtBottom = isAtBottom();
      if (userAtBottom) {
        setIsAtBottom(true);
        setShowNewMessagesPrompt(false);
      } else {
        setIsAtBottom(false);
      }
    };

    refElement.addEventListener("scroll", handleUserScroll);

    // On mount, scroll to bottom
    refElement.scrollTop = refElement.scrollHeight;
    setIsAtBottom(true);

    return () => {
      refElement.removeEventListener("scroll", handleUserScroll);
    };
  }, []);

  useEffect(() => {
    const refElement = scrollRef.current;
    if (!refElement) return;

    if (isAtBottom) {
      refElement.scrollTop = refElement.scrollHeight;
      setShowNewMessagesPrompt(false);
      setNewMessagesCount(0);
    } else if (messages.length > prevMessagesLength.current) {
      const scrollDiff = refElement.scrollHeight - prevScrollHeight.current;

      // Adjust scrollTop to keep the view stationary
      refElement.scrollTop = prevScrollTop.current + scrollDiff;
      setShowNewMessagesPrompt(true);
      setNewMessagesCount(
        (count) => count + (messages.length - prevMessagesLength.current),
      );
    } else {
      setShowNewMessagesPrompt(true);
    }

    prevMessagesLength.current = messages.length;
  }, [messages, isAtBottom, isThinking]);

  const handleScrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }

    setShowNewMessagesPrompt(false);
    setNewMessagesCount(0);
  }, []);

  return (
    <div className="nut-flex nut-relative nut-flex-1 nut-overflow-hidden nut-pt-[20px] nut-px-[0px]">
      <div className="nut-flex-1 nut-overflow-y-auto" ref={scrollRef}>
        <div className="nut-overflow-y-auto">
          {messages.map((message) => {
            const isProductMessage =
              message.message?.startsWith("products_") ||
              message.text?.startsWith("products_");

            const messageText = message.message || message.text;
            const products =
              isProductMessage && messageText
                ? JSON.parse(getValue(messageText) || "[]")
                : [];

            if (isProductMessage && products.length === 0) {
              return null; // Skip rendering if no products found
            }

            return isProductMessage ? (
              <ProductList
                key={message.id}
                productList={products || []}
                onSelectProduct={onSelectProduct}
              />
            ) : (
              <TextMessage key={message.id} message={message} />
            );
          })}
          {isThinking && <ThinkingIndicator />}
        </div>
      </div>
      <div
        className={`nut-absolute nut-bottom-4 nut-left-1/2 -nut-translate-x-1/2 nut-bg-blue-600 nut-text-white nut-px-4 nut-py-2 nut-rounded-full nut-cursor-pointer nut-shadow nut-whitespace-nowrap nut-transition-opacity ${
          showNewMessagesPrompt ? "nut-opacity-100" : "nut-opacity-0"
        }`}
        onClick={handleScrollToBottom}
      >
        {newMessagesCount > 0 ? `See recent messages` : "Scroll to bottom"}
      </div>
    </div>
  );
};
export default ChatMessages;
